import { getHotspotIcon, hotspotIconList } from '@lessonup/pin-renderer';
import { HotspotIcon, HotspotSettings } from '@lessonup/pins-shared';
import { borderRadius, color, elevationShadow, SelectField, spacing, styled } from '@lessonup/ui-components';
import { isNull } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SettingsToggle } from '../../../../../SettingsToggle/SettingsToggle';

interface IconFillPickerProps {
  selectedIcon: HotspotIcon | null;
  setSelectedIcon: (iconName: HotspotSettings['iconFill']) => void;
  suggestedIcons?: HotspotIcon[];
}

export const IconFillPicker: React.FC<IconFillPickerProps> = ({ selectedIcon, setSelectedIcon, suggestedIcons }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useTranslation('iconPicker');
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);

  // Use a set for O(1) lookup time
  const suggestedIconSet = new Set(suggestedIcons);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        eventTargetIsNode(event.target) &&
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen((prevState) => !prevState);
    if (selectRef.current) {
      selectRef.current.focus();
    }
  };

  const handleIconSelect = (icon: HotspotIcon) => {
    setSelectedIcon(icon);
    setIsDropdownOpen(false);
  };

  return (
    <SelectContainer>
      <SelectField
        size="medium"
        ref={selectRef}
        onClick={toggleDropdown}
        options={[{ label: selectedIcon ? t(getHotspotIcon(selectedIcon).translateKey) : t('selectIcon'), value: '' }]}
        iconSlot={selectedIcon && getHotspotIcon(selectedIcon).icon}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        onKeyDown={(e) => {
          e.preventDefault();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
        }}
      />
      {isDropdownOpen && (
        <Dropdown ref={dropdownRef}>
          {suggestedIcons?.map((icon, index) => (
            <SettingsToggle
              key={index}
              type="radio"
              toggleVariant="icon-only"
              ariaLabel={t('selectHotSpotIcon', { icon: index })}
              icon={getHotspotIcon(icon).icon}
              checked={icon === selectedIcon}
              onChange={() => handleIconSelect(icon)}
            />
          ))}
          {hotspotIconList.map((icon, index) =>
            suggestedIconSet.has(icon) ? null : (
              <SettingsToggle
                key={index}
                type="radio"
                toggleVariant="icon-only"
                ariaLabel={t('selectHotSpotIcon', { icon: index })}
                icon={getHotspotIcon(icon).icon}
                checked={icon === selectedIcon}
                onChange={() => handleIconSelect(icon)}
              />
            )
          )}
        </Dropdown>
      )}
    </SelectContainer>
  );
};

const SelectContainer = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: ${spacing.size8};
  padding: ${spacing.size8};
  display: grid;
  width: 100%;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;
  grid-template-columns: repeat(6, 1fr);
  gap: ${spacing.size8};
  background-color: ${color.neutral.surface.background};
  border: 1px solid ${color.neutral.outline.background};
  border-radius: ${borderRadius.rounded24};
  box-shadow: ${elevationShadow.e300};
`;

//TODO move this somewhere more generic?
function eventTargetIsNode(target: EventTarget | null): target is Node {
  return !isNull(target) && target instanceof Node;
}
